import { getCookie, setCookie, deleteCookie } from '../../utils/cookieUtils';
import { FEDERATE_LOGIN_PATH } from '../config/authConfig';
import { AppConfig } from './types';

const decodeJWT = (token: string) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return null;
  }
};

const isTokenExpired = (token: string): boolean => {
  try {
    const payload = decodeJWT(token);
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime;
  } catch {
    return true;
  }
};

const clearExpiredToken = (federateIdToken: string): void => {
  deleteCookie(federateIdToken, { path: '/' });
};

const getTokenFromQueryParams = (): string | null => {
  return new URLSearchParams(window.location.hash.slice(1)).get('id_token');
};

const getTokenFromCookie = (federateIdToken: string): string | null => {
  return getCookie(federateIdToken);
};

const setTokenToCookie = (token: string, federateIdToken: string): void => {
  setCookie(federateIdToken, token, {
    path: '/',
    expires: 3600, // 1 hour in seconds
    secure: true,
    sameSite: 'None',
  });
};

const generateNonce: () => string = () => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

const getRedirectUrl = (config: AppConfig) => {
  const identityProviderAuthEndpoint = `https://${config.identityProvider}${FEDERATE_LOGIN_PATH}`;
  const federateURL = new URL(identityProviderAuthEndpoint);
  federateURL.searchParams.append('client_id', config.federateClientId);
  const baseUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
  federateURL.searchParams.append('redirect_uri', baseUrl);
  federateURL.searchParams.append('response_type', 'id_token');
  federateURL.searchParams.append('scope', 'openid');
  federateURL.searchParams.append('nonce', generateNonce());
  return federateURL;
};

const login = (config: AppConfig) => {
  const login_url = getRedirectUrl(config);
  window.location.assign(login_url);
};

export const getUserRolesFromFederate = (token: string | null): string[] => {
  if (token) {
    const payload = decodeJWT(token);
    return payload?.role || [];
  } else {
    return [];
  }
};

export const getUserNameFromFederate = (token: string | null): string | null => {
  if (token) {
    const payload = decodeJWT(token);
    return payload?.name || null;
  } else {
    return null;
  }
};

export const getUserLoginFromFederate = (token: string | null): string | null => {
  if (token) {
    const payload = decodeJWT(token);
    return payload?.sub || null;
  } else {
    return null;
  }
};

export const getFederateToken = async (config: AppConfig): Promise<string> => {
  // First check URL for new token
  const urlToken = getTokenFromQueryParams();
  if (urlToken) {
    // Save new token to cookie and clear URL
    setTokenToCookie(urlToken, config.federateIdToken);
    window.history.replaceState({}, document.title, window.location.pathname);
    return urlToken;
  }

  // Then check cookie for existing token
  const cookieToken = getTokenFromCookie(config.federateIdToken);
  if (cookieToken) {
    // Check if token is expired
    if (isTokenExpired(cookieToken)) {
      clearExpiredToken(config.federateIdToken);
      // Redirect to login for new token
      login(config);
      throw Error('Token expired, redirecting to login.');
    }
    setTokenToCookie(cookieToken, config.federateIdToken);
    return cookieToken;
  }

  // No token found, redirect to login
  login(config);
  throw Error('No token available, redirecting to login.');
};
